<template>
  <div class="change-record-detail">
    <div v-if="!hasDesignCodes" class="change-record-drawer__state">
      <a-empty description="暂无详情设计" />
    </div>
    <div v-else-if="noPageDes" class="change-record-drawer__state">
      <a-empty description="暂无详情设计" />
    </div>
    <div v-else-if="!hasVersionId" class="change-record-drawer__state">
      <a-empty description="暂无版本数据" />
    </div>
    <template v-else>
      <a-skeleton active v-if="showSkeleton" />
      <main-page
        v-if="canMountPage"
        :key="pageMountKey"
        page-from="xmPageContent"
        :page-config="pageConfigs"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
export default defineComponent({ name: 'ChangeRecordDetailPanel' });
</script>

<script lang="ts" setup>
import { computed, onBeforeUnmount, provide, reactive, ref, toRef, unref, watch } from 'vue';
import { Empty as AEmpty, Skeleton as ASkeleton } from 'ant-design-vue';
import { deepClone } from 'xmgoing-utils';
import MainPage from '@/main-page/main-page.vue';
import { useLoadDesInfo } from '@/main-page-base/ctls/containers/xmPageContent/hooks/useLoadDesInfo';
import { usePageModelsStore } from '@/store/modules/pageModels';
import { useDataSourceStore } from '@/store/modules/dataSourceStore';
import {
  CHANGE_RECORD_DIFF_KEY,
  collectSubTableKeys,
  type ChangeRecordDiffState,
} from './changeRecordDiff';

/** 给克隆设计里的 neighbor 查询用，不占用业务 rowId */
const CHANGE_RECORD_VERSION_ID_KEY = '__changeRecordVersionId';
const NEIGHBOR_URL = '/api/xm-mes/dataHistory/neighbor';

const props = defineProps<{
  versionId: string;
  pageCode: string;
  moduleCode: string;
}>();

const pageLoadingFlag = ref(true);
const designReady = ref(false);
const pageModelsStore: any = usePageModelsStore();
const dataSourceStore: any = useDataSourceStore();

const pageCodeRef = toRef(props, 'pageCode');
const moduleCodeRef = toRef(props, 'moduleCode');

/**
 * 抽屉挂在 App，inject 不会吃到列表/审核页的 form 上下文。
 * 显式关掉流程容器和列权限，避免按当前页 pageCode 套审核 ACL / 列表字段显隐。
 */
provide('pItemType', '');
provide('formElConfigs', []);
provide('formElAclInfos', null);
provide('formKey', '');
provide('columnConfigFlag', false);
provide('mainPageColConfigCode', '');
provide('flowContainer', false);

const changeRecordDiff = reactive<ChangeRecordDiffState>({
  enabled: true,
  prevData: null,
});
provide(CHANGE_RECORD_DIFF_KEY, changeRecordDiff);

const collectFormKeys = (blocks: any[] | undefined, keys: Set<string>) => {
  if (!blocks || !Array.isArray(blocks)) return;
  blocks.forEach((item) => {
    if (item?.componentKey === 'xmForm') {
      const key = item?.model?.key;
      if (key) keys.add(String(key));
    }
    if (item?.children) {
      collectFormKeys(item.children, keys);
    }
  });
};

/**
 * 详情仍走表单自己的加载链路（onMounted → 查询 → afterLoadData），
 * 只把原先的 detailVo 换成 neighbor，入参用当前版本 id。
 */
const prepareHistoryForm = (blocks: any[] | undefined) => {
  if (!blocks || !Array.isArray(blocks)) return;
  blocks.forEach((item) => {
    if (item?.componentKey === 'xmForm' && item.dataConfig?.userDefUrl) {
      item.dataConfig.useQueryUrl = NEIGHBOR_URL;
      item.dataConfig.useQueryType = 'GET';
      item.dataConfig.changeRecordNeighbor = true;
      // 只收集本表单子树里的子表，避免把页面上其它表单的 key 写进当前快照
      item.dataConfig.changeRecordSubTableKeys = collectSubTableKeys(item.children);
      item.dataConfig.queryUserParams = [
        {
          paraName: 'versionId',
          valueType: 'vuexParams',
          filterValue: CHANGE_RECORD_VERSION_ID_KEY,
          required: true,
        },
        {
          paraName: 'direction',
          valueType: 'userDef',
          filterValue: 'PREV',
        },
      ];
    }
    if (item?.children) {
      prepareHistoryForm(item.children);
    }
  });
};

const resolveFormKeys = () => {
  const keys = new Set<string>();
  collectFormKeys(unref(pageConfigs)?.blocks, keys);
  return keys;
};

/** 打开前快照宿主页 store，关闭后还原，避免盖掉审核/详情正在用的同一 form key 和全局数据源 */
const savedFormState: Record<string, any> = {};
let savedDataSource: {
  datasourceList: any;
  dataLoadFlags: any;
  dataListCache: any;
} | null = null;
let savedVersionId: unknown;
let hostCaptured = false;

const captureHostState = () => {
  if (hostCaptured) return;
  hostCaptured = true;
  savedVersionId = pageModelsStore.state[CHANGE_RECORD_VERSION_ID_KEY];
  savedDataSource = {
    datasourceList: dataSourceStore.datasourceList,
    dataLoadFlags: dataSourceStore.dataLoadFlags,
    dataListCache: dataSourceStore.dataListCache,
  };
};

const captureFormState = () => {
  resolveFormKeys().forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(savedFormState, key)) return;
    const current = pageModelsStore.state[key];
    savedFormState[key] = current != null && typeof current === 'object'
      ? deepClone(current)
      : current;
  });
};

const restoreHostState = () => {
  Object.keys(savedFormState).forEach((key) => {
    const prev = savedFormState[key];
    if (prev === undefined) {
      delete pageModelsStore.state[key];
    } else {
      pageModelsStore.state[key] = prev;
    }
  });
  if (savedVersionId === undefined) {
    delete pageModelsStore.state[CHANGE_RECORD_VERSION_ID_KEY];
  } else {
    pageModelsStore.state[CHANGE_RECORD_VERSION_ID_KEY] = savedVersionId;
  }
  if (savedDataSource) {
    dataSourceStore.datasourceList = savedDataSource.datasourceList;
    dataSourceStore.dataLoadFlags = savedDataSource.dataLoadFlags;
    dataSourceStore.dataListCache = savedDataSource.dataListCache;
  }
};

captureHostState();

const {
  pageConfigs,
  loadPageFlag,
  noPageDes,
} = useLoadDesInfo(
  {
    events: [],
    props: {
      pageCode: props.pageCode,
      moduleCode: props.moduleCode,
      show: true,
      columnConfigFlag: false,
    },
    pageCode: unref(pageCodeRef),
    moduleCode: unref(moduleCodeRef),
    pageLoadingFlag,
  },
  (pageConfigJson) => {
    const cloned = deepClone(unref(pageConfigJson) || {});
    prepareHistoryForm(cloned.blocks);
    pageConfigJson.value = cloned;
    captureFormState();
    designReady.value = true;
  },
);

const hasDesignCodes = computed(() => {
  return !!(String(props.pageCode || '').trim() && String(props.moduleCode || '').trim());
});

const hasVersionId = computed(() => !!String(props.versionId || '').trim());

const canMountPage = computed(() => {
  return designReady.value && !noPageDes.value && hasVersionId.value;
});

const showSkeleton = computed(() => {
  if (!hasDesignCodes.value || noPageDes.value || !hasVersionId.value) {
    return false;
  }
  return loadPageFlag.value || !designReady.value;
});

const pageMountKey = computed(() => `${props.versionId}:${props.pageCode}`);

watch(
  () => props.versionId,
  (id) => {
    changeRecordDiff.prevData = null;
    pageModelsStore.state[CHANGE_RECORD_VERSION_ID_KEY] = String(id || '').trim();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  restoreHostState();
});
</script>

<style lang="less" scoped>
.change-record-detail {
  height: 100%;
  min-height: 240px;
  overflow: auto;

  :deep(tr.change-record-row-added > td),
  :deep(tr.change-record-row-added > td.ant-table-cell-fix-left) {
    background: #f6ffed !important;
  }

  :deep(tr.change-record-row-removed > td),
  :deep(tr.change-record-row-removed > td.ant-table-cell-fix-left) {
    background: #fff8f7 !important;
    color: #86909c;
  }

  :deep(tr.change-record-row-removed > td:first-child) {
    box-shadow: inset 3px 0 0 #f5222d;
  }

  :deep(tr.change-record-row-removed .editor-cell) {
    color: #86909c;
    text-decoration: line-through;
  }

  :deep(tr.change-record-row-added > td:first-child) {
    box-shadow: inset 3px 0 0 #52c41a;
  }

  :deep(tr.change-record-row-changed > td),
  :deep(tr.change-record-row-changed > td.ant-table-cell-fix-left) {
    background: #fffbe6 !important;
  }

  :deep(tr.change-record-row-changed > td:first-child) {
    box-shadow: inset 3px 0 0 #faad14;
  }

  :deep(.change-record-cell-with-prev.editor-cell) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    height: auto;
  }
}
</style>
